// Reusable CodeMirror 6 code editor component
// Provides syntax highlighting, line numbers, bracket matching, and JS syntax checking
import React, { useMemo } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { oneDark } from '@codemirror/theme-one-dark';
import { linter } from '@codemirror/lint';
import { useThemeSettings } from '../contexts/ThemeContext';

// Lightweight JS syntax linter — catches SyntaxError via new Function()
const jsLinter = linter((view) => {
  const code = view.state.doc.toString();
  if (!code.trim()) return [];
  try {
    new Function(code);
    return [];
  } catch (e) {
    if (e instanceof SyntaxError) {
      // Try to find the error position from the message
      const lineMatch = e.message.match(/line (\d+)/i);
      const line = lineMatch ? parseInt(lineMatch[1], 10) : 1;
      const lineStart = view.state.doc.line(Math.min(line, view.state.doc.lines)).from;
      const lineEnd = view.state.doc.line(Math.min(line, view.state.doc.lines)).to;
      return [{
        from: lineStart,
        to: lineEnd,
        severity: 'error',
        message: e.message.replace(/\s*\([\d:]+\)$/, ''),
      }];
    }
    return [];
  }
}, { delay: 500 });

const CodeEditor = ({ value, onChange, height = '250px', placeholder, readOnly = false }) => {
  const { themeMode } = useThemeSettings();

  const extensions = useMemo(() => [
    javascript(),
    jsLinter,
  ], []);

  return (
    <CodeMirror
      value={value}
      height={height}
      theme={themeMode === 'dark' ? oneDark : 'light'}
      extensions={extensions}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={placeholder}
      basicSetup={{
        lineNumbers: true,
        foldGutter: true,
        bracketMatching: true,
        closeBrackets: true,
        autocompletion: false,
        highlightActiveLine: true,
        indentOnInput: true,
        searchKeymap: true,
      }}
      style={{
        fontSize: '0.8rem',
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: themeMode === 'dark' ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
      }}
    />
  );
};

export default CodeEditor;
